/* eslint-disable react/jsx-one-expression-per-line */
import React, { useState, useMemo, useEffect } from 'react';
import { useMutation } from '@apollo/react-hooks';
import OrderSummary from 'shared/order-summary';
import DiscountForm from 'shared/discount-form';
import useGlobal from 'hooks/useGlobal';
import useForm from 'hooks/useForm';
import useCart from 'hooks/useCart';
import RadioButton from 'shared/form-input/radio';
import fetchFlags from 'api/flags';
import Hero from 'components/templates-default/cohort-hero';
import updateShippingRateMutation from '@/queries/checkoutShippingRate';
import Header from '../header';
import DropDown from './dropdown';
import BillingForm from './billing-form';
import PaymentForm from './payment-form';
import CustomerInfo from '../customer-info-block';
import SuspendedPurchases from './suspended-purchases';

import s from './payment.module.scss';

const requiredFields = ['lastName', 'address1', 'city', 'zip', 'province'];

function ShippingMethod({ shippingMethod, handleChange, availableShipping }) {
  return (
    <div className={s.block}>
      <p className={s.title}>Shipping Method</p>
      <div className={s.blockWrapper}>
        {availableShipping.map(({ handle, priceV2, title }) => {
          return (
            <div className={s.radio} key={handle}>
              <RadioButton
                id={handle}
                value={handle}
                checked={shippingMethod?.handle === handle}
                name="shipping-method"
                onChange={handleChange}
                controlled
              >
                <div className={s.radioChild}>
                  <p>{title}</p>
                  <p>${Number(priceV2.amount).toFixed(2)}</p>
                </div>
              </RadioButton>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function BillingAddress({ form }) {
  const [sameAsShipping, setSameAsShipping] = useState('same');
  const handleChange = e => {
    const { value } = e.target;
    return setSameAsShipping(value);
  };
  return (
    <div className={s.block}>
      <p className={s.title}>Billing Address</p>
      <div className={s.blockWrapper}>
        {['same', 'not-same'].map(type => {
          const Tag = type === 'same' ? React.Fragment : DropDown;
          const dropDownProps =
            type === 'not-same'
              ? { sideEffect: sameAsShipping === 'same' }
              : {};
          return (
            <Tag key={type} {...dropDownProps}>
              <div className={s.radio}>
                <RadioButton
                  id={type}
                  value={type}
                  checked={sameAsShipping === type}
                  name="billing-address"
                  onChange={handleChange}
                  controlled
                >
                  <div className={s.radioChild}>
                    <p>
                      {type === 'same'
                        ? 'Same as shipping address'
                        : 'Use a different billing address'}
                    </p>
                  </div>
                </RadioButton>
              </div>
              {type === 'not-same' && (
                <BillingForm
                  sameAsShipping={sameAsShipping === 'same'}
                  form={form}
                />
              )}
            </Tag>
          );
        })}
      </div>
    </div>
  );
}

export default function Payment({ customerInfo }) {
  const [suspended, setSuspended] = useState(false);
  const form = useForm({ country: 'US' }, requiredFields);
  const { emit, state: stateGlobal } = useGlobal();
  const {
    availableShipping,
    shippingRatesReady,
    shippingLine,
    checkoutId,
    shippingAddress
  } = useCart({
    withShipping: true
  });
  const [shippingMethod, setShippingMethod] = useState(
    shippingLine || shippingRatesReady ? availableShipping[0] : null
  );

  const [updateShippingRate, shippingRateData] = useMutation(
    updateShippingRateMutation
  );
  useEffect(() => {
    if (!shippingLine && shippingRatesReady) {
      setShippingMethod(availableShipping[0]);
    }
  }, [shippingRatesReady]);

  useEffect(() => {
    (async () => {
      const flags = await fetchFlags();
      const suspendedPurchases = flags && flags.suspendedPurchases;
      if (suspendedPurchases) {
        setSuspended(true);
      }
    })();
  }, []);

  const handleUpdateShippingRate = async params => {
    try {
      await updateShippingRate(params);
      if (!customerInfo.shippingLine) {
        return emit.updateCb(prevState => {
          return {
            checkout: {
              ...prevState.checkout,
              edit: null
            }
          };
        });
      }
      return null;
    } catch (error) {
      console.log(error, 'HANDLE UPDATE SHIPPING - PAYMENT');
    }
  };

  const handleChange = e => {
    const { value } = e.target;

    if (shippingRateData.loading) return null;
    return setShippingMethod(
      availableShipping.filter(({ handle }) => handle === value)[0]
    );
  };
  console.log(shippingRatesReady);

  useEffect(() => {
    if (checkoutId && shippingRatesReady) {
      handleUpdateShippingRate({
        variables: {
          checkoutId,
          shippingRateHandle: shippingMethod?.handle
        }
      });
    }
  }, [shippingMethod?.handle, checkoutId, shippingRatesReady]);
  return (
    <main className={s.root}>
      <Hero title="Checkout" />
      <Header />
      <div className="container">
        <div className={s.wrapper}>
          <div className={s.child}>
            {suspended ? (
              <SuspendedPurchases />
            ) : (
              <>
                <CustomerInfo customerInfo={customerInfo} />
                {shippingRatesReady && (
                  <ShippingMethod
                    {...{ shippingMethod, handleChange, availableShipping }}
                  />
                )}
                <BillingAddress form={form} />
                <PaymentForm
                  billingAddress={form.state.inputs}
                  checkoutId={checkoutId}
                  emitGlobal={emit}
                  stateGlobal={stateGlobal}
                />
              </>
            )}
          </div>
          <div className={s.summary}>
            <DiscountForm />
            {shippingRatesReady && (
              <OrderSummary
                options={{ withShipping: true }}
                loadShipping={shippingRateData.loading}
                shippingPrice={`$${Number(
                  shippingMethod?.priceV2?.amount
                ).toFixed(2)}`}
              />
            )}
          </div>
        </div>
      </div>
    </main>
  );
}
